/**
 * OCR result → expense form.
 *
 * The one rule that matters here: an extraction NEVER writes the `amount` object.
 * It supplies the same inputs a person types — the billing lines (whose sum is
 * the price), the discount, and the VAT flags — and computeAmount() derives base,
 * VAT, WHT and payment exactly as it does for a hand-entered record. Scanned and
 * typed records therefore cannot drift apart, because only one function does the
 * arithmetic.
 *
 * Verified against 54 real documents (2026-08-23): every one reconciled.
 */
import { computeAmount, periodFields, WHT_PRESETS } from './expense.js'

const num = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}
const r2 = (n) => Math.round((num(n) + Number.EPSILON) * 100) / 100
const clean = (v) => String(v ?? '').trim()

/** Thai tax-ID check digit — same guard the OCR endpoint applies server-side. */
export function isValidTaxId(v) {
  const d = String(v || '').replace(/\D/g, '')
  if (!/^\d{13}$/.test(d)) return false
  let sum = 0
  for (let i = 0; i < 12; i++) sum += Number(d[i]) * (13 - i)
  return (11 - (sum % 11)) % 10 === Number(d[12])
}

function uid() {
  return (crypto?.randomUUID?.() || 'x' + Date.now().toString(36) + Math.random().toString(36).slice(2))
}

/**
 * Turn an extraction into billing rows shaped like BillingTab expects.
 * `price` is derived there as price_total ÷ N, so we only carry price_total.
 */
export function ocrToBilling(x, dateISO) {
  const rows = Array.isArray(x?.billing) ? x.billing : []
  return rows
    .filter((b) => clean(b?.detail) || num(b?.price_total))
    .map((b) => {
      const n = num(b.N) > 0 ? num(b.N) : 1
      // Trust the printed line total, even when it is 0. Synthesising price × N
      // for a zero line looks helpful and is not: on several real receipts the
      // model reports a unit price for a line the document does not add into its
      // own total (a bundled or free item), and inventing that money is what
      // breaks reconciliation. The document's totals are the arbiter.
      const total = num(b.price_total)
      return {
        date: dateISO || '',
        detail: clean(b.detail),
        N: n,
        price: n ? r2(total / n) : total,
        price_total: r2(total),
        ref: clean(b.ref) || clean(x?.doc_number),
        unique_id: uid(),
        employee_id: 0,
        inventory_request_id: 0,
      }
    })
}

const isoDate = (v) => (/^\d{4}-\d{2}-\d{2}$/.test(clean(v)) ? clean(v) : '')
const digits = (v) => String(v ?? '').replace(/\D/g, '')

/**
 * Two billing rows are "the same line" when everything visible about them matches.
 * This is what lets a SECOND document be appended to a record without re-adding the
 * first document's lines when the same file is read twice.
 */
export function billingKey(row) {
  const n = num(row?.N)
  const total = r2(row?.price_total != null && row.price_total !== ''
    ? row.price_total
    : num(row?.price) * n)
  return [clean(row?.ref), clean(row?.detail), n, total.toFixed(2), clean(row?.date).slice(0, 10)].join('\u0001')
}

/**
 * Split incoming rows into the ones the record does not already hold, and the ones it
 * does.
 *
 * ⚠️ A MULTISET, NOT A SET. Real invoices do print the same line twice (two units
 * billed separately, the same service on two dates read as one). Collapsing them
 * would delete money and break reconciliation, so each existing copy cancels exactly
 * one incoming copy: re-reading a document with a doubled line still matches both.
 */
export function splitNewRows(incoming, existing) {
  const have = new Map()
  for (const r of existing || []) {
    const k = billingKey(r)
    have.set(k, (have.get(k) || 0) + 1)
  }
  const fresh = []
  const duplicates = []
  for (const r of incoming || []) {
    const k = billingKey(r)
    const n = have.get(k) || 0
    if (n > 0) { have.set(k, n - 1); duplicates.push(r) } else fresh.push(r)
  }
  return { fresh, duplicates }
}

/**
 * Build the form patch. Returns { patch, billing, warnings, fields } where
 * `fields` drives the review dialog's per-field checkboxes.
 *
 * `input` is ONE extracted document or an ARRAY of them — one file often holds a whole
 * scanned stack, and several of those invoices can belong in one expense record. The
 * documents are merged here: every line is carried, each stamped with its OWN document
 * number and date, and the amounts are summed. A single document is just an array of
 * one, mapped exactly as it always was.
 *
 * opts:
 *   mode              'append' (default) or 'replace' — what happens to lines already
 *                     in the record. Append is the one that matters: reading a second
 *                     invoice into an existing expense must ADD to it, not overwrite it.
 *   existingBilling   the record's current rows, for the duplicate check
 *   existingDiscount  the record's current discount, so appending sums instead of
 *                     replacing
 *   existingVatAdd / existingVatInclude   the record's current VAT shape
 *   currentWhtPercent / currentWhtType    left alone when the document prints no WHT
 */
export function ocrToForm(input, opts = {}) {
  const docs = (Array.isArray(input) ? input : [input]).filter(Boolean)
  const head = docs[0] || {}
  const warnings = []
  const s = head.seller || {}

  const mode = opts.mode === 'replace' ? 'replace' : 'append'
  const existing = Array.isArray(opts.existingBilling) ? opts.existingBilling : []
  // Appending only means something when there is something to append to.
  const appending = mode === 'append' && existing.length > 0

  const date = isoDate(head.date_buy)
  if (!date) warnings.push('ไม่พบวันที่ในเอกสาร — กรุณากรอกเอง')

  // Every selected document's lines, each stamped with ITS OWN date and — via
  // ocrToBilling's fallback — its own doc_number as the reference. That reference is
  // the only thing that keeps three invoices in one record separable afterwards.
  const billing = docs.flatMap((d) => ocrToBilling(d, isoDate(d?.date_buy) || date))
  const lineSum = r2(billing.reduce((t, b) => t + num(b.price_total), 0))

  // What would actually be added. In replace mode everything is; in append mode only
  // the lines the record does not already hold, so re-reading a file is a no-op.
  const { fresh, duplicates } = splitNewRows(billing, appending ? existing : [])
  const rows = mode === 'replace' ? billing : fresh
  const rowsSum = r2(rows.reduce((t, b) => t + num(b.price_total), 0))

  const amounts = docs.map((d) => d?.amount || {})
  const vat = r2(amounts.reduce((t, a) => t + num(a.VAT), 0))
  const docDiscount = r2(amounts.reduce((t, a) => t + num(a.discount), 0))
  const printedGross = r2(amounts.reduce((t, a) => t + num(a.total_price_afterVAT), 0))

  // VAT representation. The extraction reports whether the printed line prices already
  // include VAT; that flag decides which computeAmount branch to use. Documents that
  // carry VAT must AGREE — one record holds one flag, so merging an inclusive invoice
  // with an exclusive one would silently mis-state the base on one of them.
  const withVat = amounts.filter((a) => num(a.VAT) > 0)
  if (new Set(withVat.map((a) => !!a.VAT_include)).size > 1) {
    warnings.push(
      'เอกสารที่เลือกมีทั้งแบบราคารวม VAT และแบบแยก VAT — รวมเป็นรายการเดียวไม่ได้ ต้องแยกบันทึก',
    )
  }
  const VAT_include = vat > 0 && !!withVat[0]?.VAT_include
  const VAT_add = vat > 0 && !VAT_include

  // Withholding is only ever taken from what is PRINTED. A document showing no
  // WHT leaves the toggle alone — it is a payer-side decision, not a reading.
  const wh = docs.flatMap((d) => (Array.isArray(d?.withholding) ? d.withholding : []))
  const rates = [...new Set(wh.map((w) => num(w.percent)).filter((pc) => pc > 0))]
  let WHT_add = false
  let WHT_percent = opts.currentWhtPercent ?? 3
  let WHT_type = opts.currentWhtType ?? 'ค่าบริการ'
  if (rates.length) {
    WHT_add = true
    WHT_percent = rates[0] || WHT_percent
    const preset = WHT_PRESETS.find((pr) => pr.percent === WHT_percent)
    if (preset) WHT_type = preset.type
    if (rates.length > 1) {
      warnings.push(
        `เอกสารที่เลือกมีภาษีหัก ณ ที่จ่าย ${rates.length} อัตรา (${rates.map((pc) => pc + '%').join(' และ ')}) ` +
        'ระบบบันทึกได้ครั้งละอัตราเดียว — ต้องแยกเป็นหลายรายการ',
      )
    }
  }

  // Reconciliation: run the clinic's own money model over WHAT WAS READ — never over
  // the merged record — and compare with the grand total(s) printed. A mismatch means
  // the reading is internally inconsistent and a human must look. Scoping it to the
  // scan is what keeps the check meaningful when lines from an earlier document are
  // already sitting in the record.
  const derived = computeAmount({
    price: lineSum, discount: docDiscount, VAT_add, VAT_include, WHT_add, WHT_percent, WHT_type,
  })
  // One baht of rounding slack per document read, not one per scan.
  const slack = Math.max(1, docs.length)
  const reconciles = printedGross > 0 ? Math.abs(derived.total_price_afterVAT - printedGross) <= slack : null

  // Mixed-VAT invoices (rent + service, where only some lines carry VAT) are a
  // known shape this record cannot hold: computeAmount applies 7% to the whole
  // base. Detect it specifically so the message says what to do, rather than
  // just reporting that two numbers differ.
  const vatBase = r2(lineSum - docDiscount)
  const mixedVat = VAT_add && vat > 0 && vatBase > 0 && Math.abs(vat - r2(vatBase * 0.07)) > slack
  // Always show the numbers when they disagree — the mixed-VAT note explains a
  // likely cause, but it is a heuristic and can mis-diagnose a simply bad read.
  // Hiding the figures behind an explanation that might be wrong is worse than
  // showing both.
  if (reconciles === false) {
    warnings.push(
      `ยอดไม่ตรงกับเอกสาร: คำนวณได้ ${derived.total_price_afterVAT.toLocaleString('en-US', { minimumFractionDigits: 2 })} ` +
      `แต่เอกสารระบุ ${printedGross.toLocaleString('en-US', { minimumFractionDigits: 2 })} — กรุณาตรวจสอบ`,
    )
  }
  if (mixedVat) {
    warnings.push(
      'สาเหตุที่เป็นไปได้: เอกสารนี้คิด VAT เฉพาะบางรายการ (เช่น ค่าเช่าไม่มี VAT แต่ค่าบริการมี) ' +
      'ระบบคิด VAT จากยอดรวมทั้งหมด — ต้องแยกเป็นหลายรายการจึงจะตรง',
    )
  }

  // Several documents in one record: there is only one seller field and one header date.
  if (docs.length > 1) {
    const parties = [...new Set(
      docs.map((d) => digits(d?.seller?.seller_taxid) || clean(d?.seller?.seller_name)).filter(Boolean),
    )]
    if (parties.length > 1) {
      warnings.push(
        `เอกสารที่เลือกมาจากผู้ขาย ${parties.length} ราย — ระบบบันทึกผู้ขายได้รายเดียว ` +
        `จะใช้ “${clean(s.seller_name) || '—'}” จากเอกสารแรก`,
      )
    }
    const dates = [...new Set(docs.map((d) => isoDate(d?.date_buy)).filter(Boolean))]
    if (dates.length > 1) {
      warnings.push(
        `เอกสารที่เลือกมี ${dates.length} วันที่ — หัวรายการใช้ ${date || '—'} ` +
        'ส่วนแต่ละบรรทัดในรายการซื้อเก็บวันที่ของเอกสารตัวเอง',
      )
    }
  }

  // Appending a document whose VAT shape differs from the record's changes the tax
  // treatment of lines that are ALREADY there — computeAmount has one flag for the
  // whole record. Say so; do not silently flip it.
  const docVatOn = vat > 0
  const existingVatOn = !!opts.existingVatAdd || !!opts.existingVatInclude
  if (appending && rows.length && docVatOn !== existingVatOn) {
    warnings.push(docVatOn
      ? 'รายการเดิมไม่ได้คิด VAT แต่เอกสารที่อ่านมี VAT — ระบบคิด VAT จากยอดรวมทั้งหมด ตรวจสอบก่อนบันทึก'
      : 'เอกสารที่อ่านไม่มี VAT แต่รายการเดิมคิด VAT อยู่ — ระบบจะคงการคิด VAT ไว้ตามเดิม ตรวจสอบก่อนบันทึก')
  }
  if (appending && !rows.length) {
    warnings.push('ทุกบรรทัดในเอกสารนี้มีอยู่ในรายการแล้ว — ไม่มีรายการซื้อใหม่ที่จะเพิ่ม')
  }

  const taxid = digits(s.seller_taxid)
  if (taxid && !isValidTaxId(taxid)) {
    warnings.push('เลขประจำตัวผู้เสียภาษีไม่ผ่านการตรวจสอบ — อาจอ่านผิด')
  }

  if (docs.some((d) => clean(d?.doc_type) === 'สลิปโอนเงิน')) {
    warnings.push('มีสลิปโอนเงินอยู่ในเอกสารที่เลือก ซึ่งไม่ใช่ใบกำกับภาษี — ตรวจสอบว่าแนบเอกสารถูกต้อง')
  }

  // Appending a second invoice must ADD its discount to the record's, never overwrite
  // it: the money is derived from ONE discount over ALL the lines, so replacing it
  // would drop the first invoice's. Nothing new to add means nothing to add to the
  // discount either — that is what stops a re-read from inflating it.
  const existingDiscount = num(opts.existingDiscount)
  const patchDiscount = appending
    ? (rows.length ? r2(existingDiscount + docDiscount) : existingDiscount)
    : docDiscount

  // A sensible record label: the single line's description, else the vendor.
  const detail = docs.length > 1
    ? [clean(s.seller_name), `${docs.length} ฉบับ`].filter(Boolean).join(' ')
    : billing.length === 1
      ? billing[0].detail
      : [clean(s.seller_name), clean(head.doc_number)].filter(Boolean).join(' ')

  const period = date ? periodFields(date) : null

  const patch = {
    detail,
    ...(date ? { date_buy: date, ...period } : {}),
    discount: patchDiscount || '',
    VAT_add, VAT_include,
    WHT_add, WHT_percent, WHT_type,
    seller_name: clean(s.seller_name),
    seller_type: clean(s.seller_type) === 'บุคคลธรรมดา' ? 'บุคคลธรรมดา' : 'นิติบุคคล',
    seller_taxid: taxid,
    seller_phone: String(s.seller_phone || '').replace(/[^\d+]/g, ''),
    seller_address: clean(s.seller_address),
    remark: docs.map((d) => clean(d?.remark)).filter(Boolean).join(' · '),
  }

  // Per-group review rows. `flagged` marks what the model itself said to check.
  const flags = new Set(
    docs.flatMap((d) => (Array.isArray(d?.needs_review) ? d.needs_review : []))
      .map((f) => String(f).toLowerCase()),
  )
  const flaggedFor = (...keys) => keys.some((k) => [...flags].some((f) => f.includes(k)))

  // In append mode the VAT and WHT toggles are only ever turned ON by a document that
  // prints them. A no-VAT receipt added to a VAT record must not switch VAT off for
  // the lines that are already there.
  const amountApply = ['discount']
  if (!appending || docVatOn) amountApply.push('VAT_add', 'VAT_include')
  if (!appending || WHT_add) amountApply.push('WHT_add', 'WHT_percent', 'WHT_type')

  const existingSum = r2(existing.reduce((t, b) => t + (num(b?.price_total) || num(b?.price) * num(b?.N)), 0))

  const fields = [
    {
      key: 'seller', label: 'ผู้ขาย',
      value: patch.seller_name || '—',
      hint: taxid ? `เลขภาษี ${taxid}` : 'ไม่พบเลขประจำตัวผู้เสียภาษี',
      flagged: flaggedFor('seller'),
      apply: ['seller_name', 'seller_type', 'seller_taxid', 'seller_phone', 'seller_address'],
    },
    {
      key: 'date', label: 'วันที่ซื้อ',
      value: date || '—',
      hint: period ? `รอบบัญชี ${period.expense_month} ${period.expense_year}` : '',
      flagged: flaggedFor('date'),
      // expense_month (the Thai abbreviation) is derived from the number at save
      // time in ExpenseEditor.save(), so the form never holds it.
      apply: ['date_buy', 'expense_month_num', 'expense_year'],
      disabled: !date,
    },
    {
      key: 'detail', label: 'รายละเอียด',
      value: detail || '—',
      hint: docs.length > 1
        ? docs.map((d) => clean(d?.doc_number)).filter(Boolean).join(', ')
        : clean(head.doc_type) ? `${head.doc_type}${clean(head.doc_number) ? ' · ' + head.doc_number : ''}` : '',
      flagged: flaggedFor('doc_type', 'doc_number'),
      apply: ['detail'],
    },
    {
      key: 'amount', label: 'ยอดเงิน',
      value: `${derived.payment.toLocaleString('en-US', { minimumFractionDigits: 2 })} บาท`,
      hint: [
        `ก่อน VAT ${derived.total_price.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
        vat > 0 ? `VAT ${derived.VAT.toLocaleString('en-US', { minimumFractionDigits: 2 })}${VAT_include ? ' (รวมใน)' : ''}` : 'ไม่มี VAT',
        docDiscount ? `ส่วนลด ${docDiscount.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : null,
        WHT_add ? `หัก ณ ที่จ่าย ${WHT_percent}%` : null,
        // The figure above is the scanned document's own. When it is being added to a
        // record that already holds lines, say what the record becomes.
        appending && rows.length
          ? `รวมกับรายการเดิมเป็น ${r2(existingSum + rowsSum).toLocaleString('en-US', { minimumFractionDigits: 2 })} ก่อนภาษี`
          : null,
      ].filter(Boolean).join(' · '),
      flagged: flaggedFor('amount', 'vat', 'total', 'withholding') || reconciles === false,
      apply: amountApply,
    },
    {
      key: 'billing', label: 'รายการซื้อ',
      value: rows.length
        ? `${appending ? 'เพิ่ม ' : ''}${rows.length} รายการ · รวม ${rowsSum.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
        : (billing.length ? 'ไม่มีรายการใหม่ — ซ้ำกับที่มีอยู่ทั้งหมด' : '—'),
      hint: [
        rows.slice(0, 3).map((b) => b.detail).filter(Boolean).join(', ') + (rows.length > 3 ? ' …' : ''),
        duplicates.length ? `ข้าม ${duplicates.length} รายการที่มีอยู่แล้ว` : '',
        mode === 'replace' && existing.length ? `แทนที่ ${existing.length} รายการเดิม` : '',
      ].filter(Boolean).join(' · '),
      flagged: flaggedFor('billing'),
      apply: [],           // handled separately — billing is its own state
      isBilling: true,
      disabled: !rows.length,
    },
  ]

  return {
    patch, billing: rows, allBilling: billing, duplicates, mode, appending,
    warnings, fields, derived, reconciles, lineSum,
  }
}

/** Narrow a full patch down to just the groups the user ticked. */
export function pickPatch(patch, fields, chosen) {
  const out = {}
  for (const f of fields) {
    if (!chosen[f.key] || f.disabled) continue
    for (const k of f.apply) if (k in patch) out[k] = patch[k]
  }
  return out
}
